import React, { FunctionComponent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { any, map } from 'ramda';
import { FormattedMessage } from 'react-intl';
import ModalWindow from '../../components/ModalWindow';
import RegularButton from '../../components/RegularButton';
import EmailTextArea from '../../components/EmailTextArea';
import { IBindingAction, IBindingCallback1 } from '../../common/models/callback';
import { candidateSendReminderRoutine, fetchReminderTemplateRoutine } from './routines';
import { ICandidateRemind } from '../../common/models/candidate/ICandidateRemind';
import Icon from '../../components/Icon';
import { IAppState } from '../../common/models/store/IAppState';
import { IEmailTemplate } from '../../common/models/email/IEmailTemplate';
import LoaderWrapper from '../../components/LoaderWrapper';
import styles from './styles.module.scss';
import Checkbox from '../../components/Checkbox';
import { ICandidateContactInfo } from '../../common/models/candidate/ICandidateContactInfo';

interface ISendReminderModalProps {
  candidates: ICandidateContactInfo[];
  isShown: boolean;
  emailTemplate?: IEmailTemplate;
  onHide: IBindingAction;
  fetchEmailTemplate: IBindingAction;
  sendReminderR: IBindingCallback1<ICandidateRemind>;
}

const SendReminderModal: FunctionComponent<ISendReminderModalProps> = ({
  isShown,
  candidates,
  emailTemplate,
  onHide,
  fetchEmailTemplate,
  sendReminderR
}) => {
  const [emailText, setEmailText] = useState('');
  const [sendSMS, setSendSMS] = useState(false);

  useEffect(() => {
    fetchEmailTemplate();
  }, []);

  useEffect(() => {
    const anyWithPhoneNumber = any(({ phoneNumber }) => Boolean(phoneNumber), candidates);
    setSendSMS(anyWithPhoneNumber);
  }, [candidates]);

  useEffect(() => {
    if (emailTemplate) {
      setEmailText(emailTemplate.body);
    }
  }, [emailTemplate]);

  const onCancel = () => {
    if (emailTemplate) {
      setEmailText(emailTemplate.body);
    }
    onHide();
  };

  const sendReminder = () => {
    sendReminderR({ ids: map(({ id }) => id, candidates), emailText, sendSMSToAll: sendSMS });
    onCancel();
  };

  return (
    <ModalWindow
      isShown={isShown}
      onHide={onCancel}
      contentClassName={`medium-modal ${styles.modalWrapper}`}
    >
      <div className="d-flex flex-column">
        <header className={styles.previewHeader}>
          <div className={styles.previewTitle}>
            <FormattedMessage id="sendReminder.title" defaultMessage="Send reminder to candidates" />
          </div>
          <div className={styles.description}>
            <FormattedMessage
              id="messagePreview.tip"
              defaultMessage="You can change your default message in your KodiakHR settings"
            />
          </div>
        </header>
        <main className={styles.preview}>
          <LoaderWrapper loading={Boolean(!emailTemplate)}>
            <div className="mb-4">
              <Checkbox
                checked={sendSMS}
                onChange={({ target }) => setSendSMS(target.checked)}
                label={{ id: 'review.sendSMS', defaultMessage: 'Send SMS text message as well (recommended)' }}
              />
            </div>
            <EmailTextArea
              message={emailText}
              onChange={setEmailText}
              customEmailFooter={(
                <FormattedMessage id="emailPreview.candidateLink" defaultMessage="<link to provide references>" />
              )}
              customEmailHeader={(
                <FormattedMessage id="emailPreview.firstName" defaultMessage="<Candidate first name>" />
              )}
            />
          </LoaderWrapper>
        </main>
        <div className={`modal-footer-bttns ${styles.bttnsContainer}`}>
          <RegularButton
            variant="tertiary"
            textIntlId="cancel"
            defaultMessage="Cancel"
            onClick={onCancel}
          />
          <RegularButton
            disabled={!emailText.trim()}
            textIntlId="send"
            defaultMessage="Send"
            onClick={sendReminder}
            icon={<Icon icon="paperPlane" />}
          />
        </div>
      </div>
    </ModalWindow>
  );
};

const mapStateToProps = ({
  sendReminder: { emailTemplate }
}: IAppState) => ({ emailTemplate });

const mapDispachToProps = {
  sendReminderR: candidateSendReminderRoutine,
  fetchEmailTemplate: fetchReminderTemplateRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(SendReminderModal);
